import { defineStore } from 'pinia'
import { planAllows } from '~/composables/usePlanFeature'
import type { IncomingConversationShare } from '~~/shared/chat/conversationShare'

/**
 * "Shared with me" (MAR-1417): the caller's pending conversation shares — not revoked, not yet
 * continued. A share drops out of the list once it is forked; the fork joins the ordinary chat list.
 */
export const useSharedChatsStore = defineStore('sharedChats', () => {
  const { apiFetch } = useApi()
  const shares = ref<IncomingConversationShare[]>([])

  async function load() {
    // Without the chat feature the server refuses the list; without an organization there is nobody
    // to have shared anything. `planAllows` waits for the profile, so both are known here.
    if (!(await planAllows('chat'))) return
    if (!useUserProfile().profile.value?.organization_name) return
    try {
      shares.value = await apiFetch<IncomingConversationShare[]>('/api/conversations/shares')
    } catch (err) {
      // A cold "Shared with me" section, not an unhandled rejection.
      console.warn('[sharedChats] list failed:', err)
    }
  }

  function remove(shareId: string) {
    shares.value = shares.value.filter(s => s.id !== shareId)
  }

  return { shares, load, remove }
})
